import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CatalogOutputToolbar from "./CatalogOutputToolbar";
import CatalogTabStatusMessage from "./CatalogTabStatusMessage";
import ResizableHeaderCell from "./ResizableHeaderCell";
import CatalogCellEditDialog from "./CatalogCellEditDialog";
import CatalogLoadingPlaceholder from "./CatalogLoadingPlaceholder";
import { BULK_CATEGORIZE_TONE_CLASSES } from "./BulkCategorizationPanel";
import { HARD_DELETE_TONE_CLASS } from "./HardDeletePanel";
import { useResizableColumns } from "./hooks/useResizableColumns";
import { CATALOG_OUTPUT_COLUMN_WIDTHS } from "./utils/catalogTableLayout";
import {
  CATALOG_DB_COLUMN_LABELS,
  PRODUCT_COLUMN_LABELS,
  formatCatalogCell,
  catalogRowMatchesBulkRules,
  catalogRowMatchesHardDeleteRules,
} from "./utils/catalogFieldLabels";
import { defaultCatalogColumnFilters } from "./utils/columnFilterUtils";

const PRODUCT_STORAGE_KEY = "cm-catalog-visible-product-cols";
const CATALOG_STORAGE_KEY = "cm-catalog-visible-catalog-cols";

const DEFAULT_CATALOG_FILTERS = {
  uncategorizedOnly: false,
  isDisattivato: "",
  isDeleted: "",
  certificato: "",
  stato: "",
  productCodeFilter: "",
  columnFilters: defaultCatalogColumnFilters(),
};

const loadStoredColumns = (storageKey, availableColumns, fallbackColumns) => {
  const base = availableColumns?.length ? availableColumns : fallbackColumns;
  try {
    const stored = window.sessionStorage.getItem(storageKey);
    if (!stored) {
      return base;
    }
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      return base;
    }
    const filtered = parsed.filter((column) => base.includes(column));
    return filtered.length > 0 ? filtered : base;
  } catch {
    return base;
  }
};

const persistColumns = (storageKey, columns) => {
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(columns));
  } catch {
    // sessionStorage may be unavailable
  }
};

const resolveBulkToneClass = (alberoValue, rules) => {
  const normalized = String(alberoValue || "").trim();
  const rule = (rules || []).find((item) => item.alberoValue === normalized);
  if (!rule) {
    return "";
  }
  return BULK_CATEGORIZE_TONE_CLASSES[rule.colorIndex] || BULK_CATEGORIZE_TONE_CLASSES[0];
};

const CatalogDbOutputView = ({
  rows,
  productColumns,
  catalogColumns,
  tableLoading = false,
  pageOffset = 0,
  onCellSave,
  catalogFilters = DEFAULT_CATALOG_FILTERS,
  onCatalogFilterChange,
  statusMessage = "",
  statusSeverity = "info",
  readOnly = false,
  bulkCategorizeRules = [],
  onBulkCategorizeRulesChange,
  onBulkCategorizeExecuteAll,
  executingBulkCategorize = false,
  bulkCategorizeDisabled = false,
  hardDeleteRules = [],
  onHardDeleteRulesChange,
  onHardDeleteExecuteAll,
  executingHardDelete = false,
  hardDeleteDisabled = false,
  hardShowMatchesOnly = false,
  onHardShowMatchesOnlyChange,
}) => {
  const [editState, setEditState] = useState(null);
  const [draftValue, setDraftValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [bulkShowMatchesOnly, setBulkShowMatchesOnly] = useState(false);

  const allProductKeys = productColumns?.length
    ? productColumns
    : Object.keys(PRODUCT_COLUMN_LABELS);
  const allCatalogKeys = catalogColumns?.length
    ? catalogColumns
    : Object.keys(CATALOG_DB_COLUMN_LABELS);

  const [visibleProductColumns, setVisibleProductColumns] = useState(
    () => loadStoredColumns(PRODUCT_STORAGE_KEY, allProductKeys, allProductKeys),
  );
  const [visibleCatalogColumns, setVisibleCatalogColumns] = useState(
    () => loadStoredColumns(CATALOG_STORAGE_KEY, allCatalogKeys, allCatalogKeys),
  );

  useEffect(() => {
    setVisibleProductColumns((previous) => {
      const next = loadStoredColumns(PRODUCT_STORAGE_KEY, allProductKeys, allProductKeys);
      return previous.length === next.length && previous.every((col, idx) => col === next[idx])
        ? previous
        : next;
    });
  }, [allProductKeys]);

  useEffect(() => {
    setVisibleCatalogColumns((previous) => {
      const next = loadStoredColumns(CATALOG_STORAGE_KEY, allCatalogKeys, allCatalogKeys);
      return previous.length === next.length && previous.every((col, idx) => col === next[idx])
        ? previous
        : next;
    });
  }, [allCatalogKeys]);

  const productKeys = useMemo(
    () => allProductKeys.filter((column) => visibleProductColumns.includes(column)),
    [allProductKeys, visibleProductColumns],
  );
  const catalogKeys = useMemo(
    () => allCatalogKeys.filter((column) => visibleCatalogColumns.includes(column)),
    [allCatalogKeys, visibleCatalogColumns],
  );

  const displayRows = useMemo(() => {
    let base = rows || [];
    if (hardShowMatchesOnly && hardDeleteRules?.length) {
      return base;
    }
    if (bulkShowMatchesOnly && bulkCategorizeRules?.length) {
      base = base.filter((row) => catalogRowMatchesBulkRules(row, bulkCategorizeRules));
    }
    return base;
  }, [rows, hardShowMatchesOnly, hardDeleteRules, bulkShowMatchesOnly, bulkCategorizeRules]);

  useEffect(() => {
    if (bulkShowMatchesOnly && bulkCategorizeRules.length === 0) {
      setBulkShowMatchesOnly(false);
    }
  }, [bulkShowMatchesOnly, bulkCategorizeRules.length]);

  useEffect(() => {
    if (hardShowMatchesOnly && hardDeleteRules.length === 0) {
      onHardShowMatchesOnlyChange?.(false);
    }
  }, [hardShowMatchesOnly, hardDeleteRules.length, onHardShowMatchesOnlyChange]);

  useEffect(() => {
    if (hardShowMatchesOnly) {
      setBulkShowMatchesOnly(false);
    }
  }, [hardShowMatchesOnly]);

  useEffect(() => {
    if (bulkShowMatchesOnly) {
      onHardShowMatchesOnlyChange?.(false);
    }
  }, [bulkShowMatchesOnly, onHardShowMatchesOnlyChange]);

  const resizeKeys = [
    "__row__",
    ...productKeys.map((col) => `p:${col}`),
    ...catalogKeys.map((col) => `c:${col}`),
  ];

  const { getWidth, getCellStyle, startResize, tableMinWidth } = useResizableColumns(resizeKeys, {
    defaultWidths: CATALOG_OUTPUT_COLUMN_WIDTHS,
    defaultWidth: 130,
    minWidth: 72,
    storageKey: "cm-catalog-output-col-widths",
  });

  const toggleProductColumn = (column) => {
    setVisibleProductColumns((previous) => {
      const next = previous.includes(column)
        ? previous.filter((item) => item !== column)
        : [...previous, column];
      const normalized = next.length > 0 ? next : [column];
      persistColumns(PRODUCT_STORAGE_KEY, normalized);
      return normalized;
    });
  };

  const toggleCatalogColumn = (column) => {
    setVisibleCatalogColumns((previous) => {
      const next = previous.includes(column)
        ? previous.filter((item) => item !== column)
        : [...previous, column];
      const normalized = next.length > 0 ? next : [column];
      persistColumns(CATALOG_STORAGE_KEY, normalized);
      return normalized;
    });
  };

  const resetAllColumns = () => {
    setVisibleProductColumns(allProductKeys);
    setVisibleCatalogColumns(allCatalogKeys);
    persistColumns(PRODUCT_STORAGE_KEY, allProductKeys);
    persistColumns(CATALOG_STORAGE_KEY, allCatalogKeys);
  };

  const openEditor = (table, rowIndex, column, currentValue, rowMeta = {}) => {
    if (readOnly) {
      return;
    }
    setEditState({
      table,
      rowIndex,
      column,
      currentValue,
      catalogListId: rowMeta.catalogListId ?? null,
      productListId: rowMeta.productListId ?? null,
    });
    setDraftValue(currentValue == null ? "" : String(currentValue));
  };

  const closeEditor = () => {
    if (saving) {
      return;
    }
    setEditState(null);
    setDraftValue("");
  };

  const persistCellUpdate = async (state, value) => {
    if (!state || !onCellSave) {
      return;
    }
    setSaving(true);
    try {
      await onCellSave({
        table: state.table,
        rowIndex: state.rowIndex,
        column: state.column,
        value,
        catalogListId: state.catalogListId,
        productListId: state.productListId,
      });
      setEditState(null);
      setDraftValue("");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!editState) {
      return;
    }
    await persistCellUpdate(editState, draftValue);
  };

  const renderDataCell = (table, rowIndex, column, value, className, prefix, rowMeta, toneClass = "") => (
    <TableCell
      key={`${table}-${rowIndex}-${column}`}
      className={`${className}${toneClass ? ` ${toneClass}` : ""}${readOnly ? "" : " cm-editable-cell"}`.trim()}
      style={getCellStyle(`${prefix}:${column}`)}
      onDoubleClick={readOnly ? undefined : () => openEditor(table, rowIndex, column, value, rowMeta)}
      title={
        readOnly
          ? (value != null && String(value) !== "" ? String(value) : "")
          : (value != null && String(value) !== "" ? `${String(value)} (doppio click per modificare)` : "Doppio click per modificare")
      }
    >
      <Typography component="div" className="cm-cell-clamp">
        {formatCatalogCell(column, value)}
      </Typography>
    </TableCell>
  );

  return (
    <Stack spacing={2}>
      <CatalogOutputToolbar
        filters={catalogFilters}
        onFilterChange={onCatalogFilterChange}
        productColumns={allProductKeys}
        catalogColumns={allCatalogKeys}
        visibleProductColumns={visibleProductColumns}
        visibleCatalogColumns={visibleCatalogColumns}
        onToggleProductColumn={toggleProductColumn}
        onToggleCatalogColumn={toggleCatalogColumn}
        onResetColumns={resetAllColumns}
        bulkCategorizeRules={bulkCategorizeRules}
        onBulkCategorizeRulesChange={onBulkCategorizeRulesChange}
        onBulkCategorizeExecuteAll={onBulkCategorizeExecuteAll}
        executingBulkCategorize={executingBulkCategorize}
        bulkCategorizeDisabled={bulkCategorizeDisabled}
        bulkShowMatchesOnly={bulkShowMatchesOnly}
        onBulkShowMatchesOnlyChange={setBulkShowMatchesOnly}
        hardDeleteRules={hardDeleteRules}
        onHardDeleteRulesChange={onHardDeleteRulesChange}
        onHardDeleteExecuteAll={onHardDeleteExecuteAll}
        executingHardDelete={executingHardDelete}
        hardDeleteDisabled={hardDeleteDisabled}
        hardShowMatchesOnly={hardShowMatchesOnly}
        onHardShowMatchesOnlyChange={onHardShowMatchesOnlyChange}
      />

      <CatalogTabStatusMessage message={statusMessage} severity={statusSeverity} />

      <TableContainer
        className={`cm-table-wrap cm-catalog-output-scroll${tableLoading ? " is-loading" : ""}`}
      >
        {tableLoading ? (
          <CatalogLoadingPlaceholder />
        ) : (
        <Table
          size="small"
          stickyHeader
          className="cm-table cm-catalog-output-table"
          sx={{ tableLayout: "fixed", minWidth: tableMinWidth, width: tableMinWidth }}
        >
          <TableHead>
            <TableRow className="cm-catalog-output-section-row">
              <TableCell
                rowSpan={2}
                className="cm-catalog-db-row-head cm-catalog-output-index-head cm-sticky-cell"
                style={getCellStyle("__row__")}
              >
                #
              </TableCell>
              <TableCell
                colSpan={Math.max(productKeys.length, 1)}
                className="cm-catalog-output-section-head cm-catalog-output-section-products"
              >
                products
              </TableCell>
              <TableCell
                colSpan={Math.max(catalogKeys.length, 1)}
                className="cm-catalog-output-section-head cm-catalog-output-section-catalogs"
              >
                catalogs
              </TableCell>
            </TableRow>
            <TableRow className="cm-catalog-output-columns-row">
              {productKeys.map((column, columnIndex) => (
                <ResizableHeaderCell
                  key={`p-head-${column}`}
                  columnKey={`p:${column}`}
                  width={getWidth(`p:${column}`)}
                  onResizeStart={startResize}
                  className={`cm-catalog-db-col-products cm-sticky-cell${
                    columnIndex === productKeys.length - 1 ? " cm-catalog-output-products-edge" : ""
                  }`}
                >
                  {PRODUCT_COLUMN_LABELS[column] || column}
                </ResizableHeaderCell>
              ))}
              {catalogKeys.map((column) => (
                <ResizableHeaderCell
                  key={`c-head-${column}`}
                  columnKey={`c:${column}`}
                  width={getWidth(`c:${column}`)}
                  onResizeStart={startResize}
                  className="cm-catalog-db-col-catalogs cm-sticky-cell"
                >
                  {CATALOG_DB_COLUMN_LABELS[column] || column}
                </ResizableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.map((row, index) => {
              const absoluteIndex = pageOffset + index;
              const rowMeta = {
                catalogListId: row.catalog?.catalog_list_id ?? null,
                productListId: row.product?.product_list_id ?? null,
                currentPath: row.catalog?.albero_categorie ?? "",
                productName: row.product?.nome ?? "",
                productDescription: row.catalog?.descrizione ?? "",
              };
              const toneClass = catalogRowMatchesHardDeleteRules(row, hardDeleteRules)
                ? HARD_DELETE_TONE_CLASS
                : resolveBulkToneClass(row.catalog?.albero_categorie, bulkCategorizeRules);
              return (
              <TableRow key={`catalog-output-${row.catalog?.catalog_list_id || row.product?.product_list_id || index}`}>
                <TableCell
                  className={`cm-catalog-db-row-index cm-sticky-cell${toneClass ? ` ${toneClass}` : ""}`.trim()}
                  style={getCellStyle("__row__")}
                >
                  {absoluteIndex + 1}
                </TableCell>
                {productKeys.map((column, columnIndex) =>
                  renderDataCell(
                    "product",
                    absoluteIndex,
                    column,
                    row.product?.[column],
                    `cm-catalog-db-cell-products${
                      columnIndex === productKeys.length - 1 ? " cm-catalog-output-products-edge" : ""
                    }`,
                    "p",
                    rowMeta,
                    toneClass,
                  ),
                )}
                {catalogKeys.map((column) =>
                  renderDataCell(
                    "catalog",
                    absoluteIndex,
                    column,
                    row.catalog?.[column],
                    "cm-catalog-db-cell-catalogs",
                    "c",
                    rowMeta,
                    toneClass,
                  ),
                )}
              </TableRow>
              );
            })}
            {displayRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={productKeys.length + catalogKeys.length + 1} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        )}
      </TableContainer>

      <CatalogCellEditDialog
        open={Boolean(editState)}
        table={editState?.table}
        column={editState?.column}
        currentValue={editState?.currentValue}
        draftValue={draftValue}
        saving={saving}
        onClose={closeEditor}
        onChange={setDraftValue}
        onSave={handleSave}
      />

    </Stack>
  );
};

CatalogDbOutputView.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  productColumns: PropTypes.arrayOf(PropTypes.string),
  catalogColumns: PropTypes.arrayOf(PropTypes.string),
  tableLoading: PropTypes.bool,
  pageOffset: PropTypes.number,
  onCellSave: PropTypes.func,
  catalogFilters: PropTypes.shape({
    uncategorizedOnly: PropTypes.bool,
    isDisattivato: PropTypes.string,
    isDeleted: PropTypes.string,
    certificato: PropTypes.string,
    stato: PropTypes.string,
    productCodeFilter: PropTypes.string,
    columnFilters: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      columnName: PropTypes.string,
      columnMode: PropTypes.string,
      columnValue: PropTypes.string,
    })),
  }),
  onCatalogFilterChange: PropTypes.func,
  statusMessage: PropTypes.string,
  statusSeverity: PropTypes.oneOf(["error", "warning", "info", "success"]),
  readOnly: PropTypes.bool,
  bulkCategorizeRules: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    alberoValue: PropTypes.string,
    colorIndex: PropTypes.number,
  })),
  onBulkCategorizeRulesChange: PropTypes.func,
  onBulkCategorizeExecuteAll: PropTypes.func,
  executingBulkCategorize: PropTypes.bool,
  bulkCategorizeDisabled: PropTypes.bool,
  hardDeleteRules: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    table: PropTypes.string,
    column: PropTypes.string,
    value: PropTypes.string,
  })),
  onHardDeleteRulesChange: PropTypes.func,
  onHardDeleteExecuteAll: PropTypes.func,
  executingHardDelete: PropTypes.bool,
  hardDeleteDisabled: PropTypes.bool,
  hardShowMatchesOnly: PropTypes.bool,
  onHardShowMatchesOnlyChange: PropTypes.func,
};

export default CatalogDbOutputView;
